import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { CategoryTreeType } from '../../models'
import { ActivatedRoute, Router } from '@angular/router'
import { Category } from 'depoto-core/src/entities'
import { NotificationService } from '../../services'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { CategoryService } from '../../services/entity-services/category.service'

export const categorySchema = {
  id: null,
  externalId: null,
  name: null,
  text: null,
  parent: {
    id: null,
  },
  children: {
    id: null,
    name: null,
  },
  hasChildren: null,
  position: null,
  company: {
    id: null,
    name: null,
  },
  treePath: null,
  tags: {
    id: null,
    name: null,
  },
}

@Component({
  selector: 'category',
  templateUrl: 'category.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CategoryComponent implements CheckDataChanges, OnDestroy {
  private categoryService = inject(CategoryService)
  private queryClient = injectQueryClient()

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  categoryTreeTypeEnum = signal(CategoryTreeType)
  submitted = signal(false)

  categoryForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
    text: new FormControl({ value: '', disabled: false }, []),
    externalId: new FormControl({ value: '', disabled: false }, []),
    parent: new FormControl({ value: new Category(), disabled: false }, []),
  })

  private initialFormValue = signal<any>(null)

  readonly queryCategory = injectQuery(() => ({
    queryKey: ['category', Number(this.activatedRoute.snapshot.params['id'])], // todo categoryId
    queryFn: async () => {
      const id = this.activatedRoute.snapshot.params['id']
      if (id) {
        return await this.categoryService.getCategory(id, categorySchema)
      } else {
        return new Category()
      }
    },
    onSuccess: () => {
      this.fillFormValue()
    },
  }))

  isCreating = computed(() => {
    return !this.activatedRoute.snapshot.params['id']
  })

  createCategory = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)
      if (this.categoryForm.invalid) {
        throw new Error('Form is invalid')
      }
      const n = this.getDataToSave()
      return this.categoryService.createCategory(n, categorySchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.categoryForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.categoryForm.markAsPristine()

      this.notificatonService.success(this.translateService.instant('alert.category.created'))

      // Update the detail cache for this category so consumers see fresh data immediately
      this.queryClient.setQueryData(['category', Number(data['id'])], data)

      this.removeCategoryListings()

      // Navigate to detail page
      this.router.navigate(['/category', data['id']], { replaceUrl: true })
    },
  }))

  updateCategory = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)
      if (this.categoryForm.invalid) {
        throw new Error('Form is invalid')
      }
      const categoryDataToSave = this.getDataToSave()
      return this.categoryService.updateCategory(categoryDataToSave, categorySchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.categoryForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.categoryForm.markAsPristine()

      this.notificatonService.success(this.translateService.instant('alert.category.saved'))

      // Update the detail cache for this category with returned server data
      this.queryClient.setQueryData(['category', data['id']], data)

      this.removeCategoryListings()

      // Navigate to detail (keeping behavior consistent)
      this.router.navigate(['/category', data['id']])
    },
  }))

  deleteCategory = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      await this.categoryService.deleteCategory(this.queryCategory.data()).then(() => {
        this.notificatonService.success(this.translateService.instant('alert.category.deleted'))
        const idStr = String(this.activatedRoute.snapshot.params['id'])
        // OPTIONS: update options cache by removing the category
        this.removeCategoryListings()

        // Navigate back to listing
        this.router.navigate(['/categories'], { replaceUrl: true })
      })
    },
  }))

  loading = computed(() => {
    return this.queryCategory.isFetching() || this.createCategory.isPending() || this.updateCategory.isPending()
  })

  constructor(
    private activatedRoute: ActivatedRoute,
    public router: Router,
    private notificatonService: NotificationService,
    private translateService: TranslateService
  ) {
    effect(() => {
      const data = this.queryCategory.data()
      if (data) {
        const formValue = {
          name: data.name,
          text: data.text,
          externalId: data.externalId,
          parent: data.parent,
        }

        this.categoryForm.patchValue(formValue, { emitEvent: false })
        this.initialFormValue.set(structuredClone(formValue))
        this.categoryForm.markAsPristine()
      }
    })
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different category ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  editedCategoryChanged(event: Category | Partial<Category>) {
    // Update the parent form control when category tree changes
    if (event) {
      this.categoryForm.patchValue({ parent: event.parent ?? null })
    }
  }

  goBack(): void {
    // Use Router navigation to ensure CanDeactivate guard runs consistently
    this.router.navigate(['/categories'])
  }

  getDataToSave() {
    const currentValues = this.categoryForm.getRawValue()
    const initial = this.initialFormValue()
    const changedData: any = {}

    // Always include id
    if (this.queryCategory.data()?.id) {
      changedData.id = this.queryCategory.data().id
    }

    // Always include required fields
    changedData.name = currentValues.name

    // Include only changed optional fields
    if (currentValues.text !== initial?.text) {
      changedData.text = currentValues.text
    }
    if (currentValues.externalId !== initial?.externalId) {
      changedData.externalId = currentValues.externalId
    }

    // Compare parent IDs properly
    const currentParentId = currentValues.parent?.id || null
    const initialParentId = initial?.parent?.id || null
    if (currentParentId !== initialParentId) {
      changedData.parent = currentValues.parent
    }

    return changedData
  }

  getDirtyValues(form: any) {
    const dirtyValues = {}

    Object.keys(form.controls).forEach(key => {
      const currentControl = form.controls[key]

      if (currentControl.dirty) {
        if (currentControl.controls) dirtyValues[key] = this.getDirtyValues(currentControl)
        else dirtyValues[key] = currentControl.value
      }
    })

    return dirtyValues
  }

  public fillFormValue() {
    this.categoryForm.setValue({
      name: this.queryCategory.data().name,
      text: this.queryCategory.data().text,
      externalId: this.queryCategory.data().externalId,
      parent: this.queryCategory.data().parent,
    })
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.categoryForm.getRawValue()
    const initial = this.initialFormValue()

    // If initial is not yet set (e.g., brand-new form), fall back to form dirty flag
    if (!initial) {
      return this.categoryForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))
    const currentParentId = current?.parent?.id ?? null
    const initialParentId = initial?.parent?.id ?? null

    const changed =
      norm(current?.name) !== norm(initial?.name) ||
      norm(current?.text) !== norm(initial?.text) ||
      norm(current?.externalId) !== norm(initial?.externalId) ||
      currentParentId !== initialParentId

    return changed
  }

  private removeCategoryListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['categories'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.categoryForm.controls
  }
}
